import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, Image, Loader2, Car, ArrowRight, ArrowLeft, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUEL_TYPES = ["Essence", "Diesel", "Hybride", "Electrique", "GPL"];
const TRANSMISSIONS = ["Manuelle", "Automatique"];
const CONDITIONS = ["Excellent", "Tres bon", "Bon", "Correct", "A reviser"];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 35 }, (_, i) => currentYear - i);

export default function NewEstimation({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const [form, setForm] = useState({
    brand: "",
    model: "",
    year: "",
    mileage: "",
    fuel_type: "",
    transmission: "",
    condition: "",
    description: "",
  });

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await axios.get(`${API}/car-brands`, { withCredentials: true });
        setBrands(res.data.brands || []);
      } catch (err) {
        console.error("Fetch brands error:", err);
      }
    };
    fetchBrands();
  }, []);

  useEffect(() => {
    if (!form.brand) { setModels([]); return; }
    const fetchModels = async () => {
      try {
        const res = await axios.get(`${API}/car-models/${form.brand}`, { withCredentials: true });
        setModels(res.data.models || []);
      } catch (err) {
        console.error("Fetch models error:", err);
      }
    };
    fetchModels();
  }, [form.brand]);

  const handleFileUpload = useCallback((files) => {
    const validFiles = Array.from(files).filter(f => f.type.startsWith("image/")).slice(0, 5 - photos.length);
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => {
          if (prev.length >= 5) return prev;
          return [...prev, { data: e.target.result, name: file.name }];
        });
      };
      reader.readAsDataURL(file);
    });
  }, [photos.length]);

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        brand: form.brand,
        model: form.model,
        year: parseInt(form.year),
        mileage: parseInt(form.mileage),
        fuel_type: form.fuel_type,
        transmission: form.transmission,
        condition: form.condition,
        description: form.description,
        photos: photos.map(p => p.data),
      };
      const res = await axios.post(`${API}/estimations`, payload, { withCredentials: true });
      navigate(`/estimation/${res.data.estimation_id}`);
    } catch (err) {
      console.error("Submit error:", err);
      setSubmitting(false);
    }
  };

  const isStep1Valid = form.brand && form.model && form.year && form.mileage;
  const isStep2Valid = form.fuel_type && form.transmission && form.condition;

  return (
    <div className="min-h-screen bg-white" data-testid="new-estimation-page">
      <Header user={user} />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-10">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  step >= s
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-400"
                }`}
                data-testid={`step-indicator-${s}`}
              >
                {s}
              </div>
              <span className={`text-sm font-medium hidden sm:inline ${step >= s ? "text-slate-900" : "text-slate-400"}`}>
                {s === 1 ? "Vehicule" : s === 2 ? "Details" : "Photos"}
              </span>
              {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? "bg-slate-900" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Vehicle Info */}
        {step === 1 && (
          <div className="animate-fade-in-up" data-testid="step-1">
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Informations du vehicule
            </h2>
            <p className="text-slate-600 mb-8">Renseignez les informations principales de votre voiture.</p>

            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Marque</Label>
                <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v, model: "" })}>
                  <SelectTrigger className="h-12 rounded-lg border-slate-200" data-testid="select-brand">
                    <SelectValue placeholder="Selectionnez une marque" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Modele</Label>
                <Select value={form.model} onValueChange={(v) => setForm({ ...form, model: v })} disabled={!form.brand}>
                  <SelectTrigger className="h-12 rounded-lg border-slate-200" data-testid="select-model">
                    <SelectValue placeholder={form.brand ? "Selectionnez un modele" : "Selectionnez d'abord une marque"} />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Annee</Label>
                  <Select value={form.year} onValueChange={(v) => setForm({ ...form, year: v })}>
                    <SelectTrigger className="h-12 rounded-lg border-slate-200" data-testid="select-year">
                      <SelectValue placeholder="Annee" />
                    </SelectTrigger>
                    <SelectContent>
                      {YEARS.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Kilometrage</Label>
                  <Input
                    type="number"
                    placeholder="ex: 85000"
                    value={form.mileage}
                    onChange={(e) => setForm({ ...form, mileage: e.target.value })}
                    className="h-12 rounded-lg border-slate-200"
                    data-testid="input-mileage"
                  />
                </div>
              </div>
            </div>

            <div className="mt-10 flex justify-end">
              <Button
                onClick={() => setStep(2)}
                disabled={!isStep1Valid}
                className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-8 py-3 h-auto btn-press"
                data-testid="step1-next-button"
              >
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div className="animate-fade-in-up" data-testid="step-2">
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Details du vehicule
            </h2>
            <p className="text-slate-600 mb-8">Precisions techniques et etat general.</p>

            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Carburant</Label>
                <Select value={form.fuel_type} onValueChange={(v) => setForm({ ...form, fuel_type: v })}>
                  <SelectTrigger className="h-12 rounded-lg border-slate-200" data-testid="select-fuel">
                    <SelectValue placeholder="Type de carburant" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUEL_TYPES.map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Boite de vitesses</Label>
                <Select value={form.transmission} onValueChange={(v) => setForm({ ...form, transmission: v })}>
                  <SelectTrigger className="h-12 rounded-lg border-slate-200" data-testid="select-transmission">
                    <SelectValue placeholder="Type de boite" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSMISSIONS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Etat general</Label>
                <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                  <SelectTrigger className="h-12 rounded-lg border-slate-200" data-testid="select-condition">
                    <SelectValue placeholder="Etat du vehicule" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Description (optionnel)</Label>
                <Textarea
                  placeholder="Options, historique d'entretien, reparations recentes..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="min-h-[100px] rounded-lg border-slate-200"
                  data-testid="input-description"
                />
              </div>
            </div>

            <div className="mt-10 flex justify-between">
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className="text-slate-600 font-medium"
                data-testid="step2-back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!isStep2Valid}
                className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-8 py-3 h-auto btn-press"
                data-testid="step2-next-button"
              >
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Photos */}
        {step === 3 && (
          <div className="animate-fade-in-up" data-testid="step-3">
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Photos du vehicule
            </h2>
            <p className="text-slate-600 mb-8">
              Ajoutez des photos pour une estimation plus precise (optionnel, max 5).
            </p>

            {/* Upload zone */}
            {photos.length < 5 && (
              <label
                className={`upload-zone flex flex-col items-center justify-center p-10 cursor-pointer ${dragOver ? "drag-over" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files); }}
                data-testid="photo-upload-zone"
              >
                <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                  <Camera className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-700 mb-1">Cliquez ou glissez vos photos</p>
                <p className="text-xs text-slate-400">JPEG, PNG, WEBP - Max 5 photos</p>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  data-testid="photo-file-input"
                />
              </label>
            )}

            {/* Photo previews */}
            {photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6" data-testid="photo-preview-grid">
                {photos.map((photo, i) => (
                  <div key={i} className="relative group rounded-xl overflow-hidden aspect-[4/3]">
                    <img
                      src={photo.data}
                      alt={`Photo ${i + 1}`}
                      className="w-full h-full object-cover photo-thumb"
                    />
                    <button
                      onClick={() => removePhoto(i)}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-photo-${i}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/50 text-white text-xs">
                      {i + 1}/{photos.length}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary card */}
            <div className="mt-8 bg-slate-50 rounded-xl p-6 border border-slate-100" data-testid="estimation-summary">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Recapitulatif
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 receipt-line">
                  <span className="text-slate-500">Vehicule</span>
                  <span className="font-medium text-slate-900">{form.brand} {form.model}</span>
                </div>
                <div className="flex justify-between py-2 receipt-line">
                  <span className="text-slate-500">Annee</span>
                  <span className="font-medium text-slate-900">{form.year}</span>
                </div>
                <div className="flex justify-between py-2 receipt-line">
                  <span className="text-slate-500">Kilometrage</span>
                  <span className="font-medium text-slate-900">{parseInt(form.mileage).toLocaleString("fr-FR")} km</span>
                </div>
                <div className="flex justify-between py-2 receipt-line">
                  <span className="text-slate-500">Carburant</span>
                  <span className="font-medium text-slate-900">{form.fuel_type}</span>
                </div>
                <div className="flex justify-between py-2 receipt-line">
                  <span className="text-slate-500">Boite</span>
                  <span className="font-medium text-slate-900">{form.transmission}</span>
                </div>
                <div className="flex justify-between py-2 receipt-line">
                  <span className="text-slate-500">Etat</span>
                  <span className="font-medium text-slate-900">{form.condition}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-500">Photos</span>
                  <span className="font-medium text-slate-900">{photos.length} photo{photos.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>

            <div className="mt-10 flex justify-between">
              <Button
                variant="ghost"
                onClick={() => setStep(2)}
                className="text-slate-600 font-medium"
                data-testid="step3-back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-8 py-3 h-auto btn-press"
                data-testid="submit-estimation-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Estimation en cours...
                  </>
                ) : (
                  <>
                    <Car className="w-4 h-4 mr-2" />
                    Lancer l'estimation
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
